/* eslint-disable */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, IdCard, Activity as ActivityIcon, Lock } from "lucide-react";
import api from "@/lib/api";
import {
  Panel, StatusPill, EmptyState, Loading, SectionLabel, Kpi, HEALTH_LABELS,
} from "@/components/app/primitives";
import { Delta } from "@/components/app/mc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  moneyCompact, moneyFull, pct, months as monthsFmt, ratioLabel, dueLabel, relativeTime, EMPTY_MARK,
} from "@/lib/format";

const RATIO_TONE = { on_track: "on_track", at_risk: "at_risk", blocked: "blocked", unknown: "neutral" };

function rangeLabel(startISO, endISO) {
  try {
    const opt = { day: "numeric", month: "short" };
    const s = new Date(startISO).toLocaleDateString("en-GB", opt);
    const e = new Date(endISO).toLocaleDateString("en-GB", opt);
    return `${s} to ${e}`;
  } catch (err) { return ""; }
}

function VentureSummaryCard({ v }) {
  if (v.restricted) {
    return (
      <div data-testid="digest-venture-card" className="rounded-md border border-border bg-card p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-lg text-foreground leading-none">{v.name}</p>
          <StatusPill tone={v.health} label={HEALTH_LABELS[v.health] || v.health} />
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" strokeWidth={1.75} /> Metrics restricted</p>
      </div>
    );
  }
  return (
    <div data-testid="digest-venture-card" className="rounded-md border border-border bg-card p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      <div className="flex items-center justify-between gap-2">
        <p className="font-display text-lg text-foreground leading-none">{v.name}</p>
        <StatusPill tone={v.health} label={HEALTH_LABELS[v.health] || v.health} />
      </div>
      {v.has_metrics === false ? (
        <p className="mt-3 text-xs text-muted-foreground">No metrics recorded yet.</p>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <SectionLabel>Revenue</SectionLabel>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="font-mono tabular-nums text-sm text-foreground">{moneyCompact(v.revenue)}</span>
              <Delta value={v.revenue_delta} format={(x) => moneyCompact(x)} />
            </div>
          </div>
          <div>
            <SectionLabel>Runway</SectionLabel>
            <p className="mt-0.5 font-mono tabular-nums text-sm text-foreground">{monthsFmt(v.runway_months)}</p>
          </div>
          <div>
            <SectionLabel>Churn</SectionLabel>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="font-mono tabular-nums text-sm text-foreground">{pct(v.churn_rate)}</span>
              <Delta value={v.churn_delta} invert suffix="%" format={(x) => x.toFixed(1)} />
            </div>
          </div>
          <div>
            <SectionLabel>LTV:CAC</SectionLabel>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="font-mono tabular-nums text-sm text-foreground">{ratioLabel(v.ltv_cac_ratio)}</span>
              <StatusPill tone={RATIO_TONE[v.ltv_cac_status]} dot={false} label={v.ltv_cac_status === "blocked" ? "low" : v.ltv_cac_status === "at_risk" ? "watch" : v.ltv_cac_status === "on_track" ? "ok" : "-"} className="px-1.5 py-0.5 text-[9px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WeeklyDigest() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("studio");
  const [ventures, setVentures] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/mission-control/overview")
      .then((res) => setVentures(res.data.ventures || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = scope === "studio" ? "/digest/studio" : `/digest/venture/${scope}`;
      const res = await api.get(url);
      setData(res.data);
    } catch (e) {
      toast.error("Could not load the weekly digest.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button data-testid="digest-back" onClick={() => navigate("/mission-control")} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} /> Mission Control
        </button>
        <div className="flex items-center gap-3">
          {data && <span className="font-mono text-xs text-muted-foreground">{rangeLabel(data.week_start, data.week_end)}</span>}
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="digest-scope-select" className="h-9 w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="studio">Studio wide</SelectItem>
              {ventures.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <Loading label="Loading weekly digest" />
      ) : !data ? (
        <EmptyState message="Nothing to summarise yet." />
      ) : scope === "studio" ? (
        <div className="space-y-5" data-testid="digest-studio">
          {data.capital && (
            <Panel title="Studio capital position" bodyClassName="p-4">
              <div className="grid grid-cols-3 gap-4">
                <Kpi label="Cash on hand" value={moneyCompact(data.capital.cash_on_hand)} testId="digest-capital-cash" />
                <Kpi label="Monthly burn" value={moneyCompact(data.capital.monthly_burn)} />
                <Kpi label="Runway" value={monthsFmt(data.capital.runway_months)} />
              </div>
            </Panel>
          )}

          <Panel title="Ventures this week" testId="digest-ventures-panel" bodyClassName="p-4">
            {data.ventures.length === 0 ? <EmptyState message="No ventures visible to you." /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {data.ventures.map((v) => <VentureSummaryCard key={v.id} v={v} />)}
              </div>
            )}
          </Panel>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-6">
              <Panel title="Upcoming milestones" bodyClassName="p-2">
                {data.milestones.length === 0 ? <EmptyState message="No milestones in the next three weeks." /> : (
                  <ul className="divide-y divide-border">
                    {data.milestones.map((m, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 px-2 py-2.5">
                        <span className="flex items-center gap-2 min-w-0">
                          <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                          <span className="truncate text-sm text-foreground">{m.venture}</span>
                          <span className="truncate text-xs text-muted-foreground">{m.milestone || EMPTY_MARK}</span>
                        </span>
                        <StatusPill tone={m.days < 0 ? "blocked" : m.days <= 7 ? "at_risk" : "neutral"} dot={false} label={dueLabel(m.due)} className="px-1.5 py-0.5 text-[9px]" />
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>
            <div className="col-span-12 lg:col-span-6">
              <Panel title="Work pass reminders" bodyClassName="p-2">
                {data.work_passes.length === 0 ? <EmptyState message="No passes expiring in the next 60 days." /> : (
                  <ul className="divide-y divide-border">
                    {data.work_passes.map((w, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 px-2 py-2.5">
                        <span className="flex items-center gap-2 min-w-0">
                          <IdCard className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                          <span className="truncate text-sm text-foreground">{w.person || EMPTY_MARK}</span>
                          <span className="truncate text-xs text-muted-foreground">{w.pass_type}</span>
                        </span>
                        <StatusPill tone={w.days <= 14 ? "at_risk" : "neutral"} dot={false} label={dueLabel(w.expires_on)} className="px-1.5 py-0.5 text-[9px]" />
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>
          </div>

          <Panel title="Activity this week" bodyClassName="p-2">
            {data.activity.length === 0 ? <EmptyState message="No recorded activity this week." /> : (
              <ul className="divide-y divide-border">
                {data.activity.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 px-2 py-2.5">
                    <span className="flex items-center gap-2 min-w-0">
                      <ActivityIcon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                      <span className="truncate text-sm text-foreground">{a.actor}</span>
                      <span className="truncate text-xs text-muted-foreground">{a.action} {a.target}</span>
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground shrink-0">{relativeTime(a.timestamp)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      ) : (
        <div className="space-y-5" data-testid="digest-venture">
          <Panel title={`${data.venture.name} this week`} bodyClassName="p-4">
            <VentureSummaryCard v={data.summary} />
          </Panel>
          <Panel title="Activity this week" bodyClassName="p-2">
            {data.activity.length === 0 ? <EmptyState message="No recorded activity this week." /> : (
              <ul className="divide-y divide-border">
                {data.activity.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 px-2 py-2.5">
                    <span className="flex items-center gap-2 min-w-0">
                      <ActivityIcon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                      <span className="truncate text-sm text-foreground">{a.actor}</span>
                      <span className="truncate text-xs text-muted-foreground">{a.action} {a.target}</span>
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground shrink-0">{relativeTime(a.timestamp)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
